import { ProjectActions, ProjectRoles, TeamActions, TeamRoles } from "@domain/schemas/workspace";
import type { ProjectAction, ProjectRoleName, TeamAction, TeamRoleName } from "@domain/schemas/workspace";

/**
 * Mirrors the backend's `TeamRoleActionMatrix`/`ProjectRoleActionMatrix` (`FR-UAUTH-PERMISSIONS-006`/`-007`).
 *
 * `GET /me/permissions` already strips entitlement-gated actions out of its response with no
 * signal as to why (see `MePermissions`'s doc comment) - comparing the server's returned actions
 * against what this static matrix says the role *should* grant is the only way the frontend can
 * tell "your role would normally grant this, but it's missing" (an entitlement gap, WP8) apart
 * from "your role simply doesn't grant this" (an ordinary permission denial).
 */
const TEAM_ROLE_ACTIONS: Record<TeamRoleName, ReadonlySet<TeamAction>> = {
  [TeamRoles.Member]: new Set([TeamActions.ViewInventory]),
  [TeamRoles.ProjectLead]: new Set([TeamActions.ViewInventory, TeamActions.CreateProject, TeamActions.DeleteProject, TeamActions.AssignProjectRoles, TeamActions.RenameProject]),
  [TeamRoles.Manager]: new Set([
    TeamActions.ViewInventory,
    TeamActions.CreateProject,
    TeamActions.DeleteProject,
    TeamActions.AssignProjectRoles,
    TeamActions.RenameProject,
    TeamActions.RenameTeam,
    TeamActions.ManageMembership,
    TeamActions.AssignTeamRoles,
    TeamActions.ManageStorageConfiguration,
    TeamActions.ManageSoftwareVersions,
  ]),
};

const PROJECT_ROLE_ACTIONS: Record<ProjectRoleName, ReadonlySet<ProjectAction>> = {
  [ProjectRoles.Reader]: new Set([ProjectActions.View]),
  [ProjectRoles.Operator]: new Set([ProjectActions.View, ProjectActions.TriggerJob, ProjectActions.DeleteJobArtifacts]),
  [ProjectRoles.Manager]: new Set([
    ProjectActions.View,
    ProjectActions.TriggerJob,
    ProjectActions.DeleteJobArtifacts,
    ProjectActions.ManageMembership,
    ProjectActions.Rename,
    ProjectActions.ManageStorageConfiguration,
    ProjectActions.ManageSoftwareVersions,
  ]),
};

export const isTeamActionAllowedByRole = (role: TeamRoleName, action: TeamAction): boolean => TEAM_ROLE_ACTIONS[role].has(action);

export const isProjectActionAllowedByRole = (role: ProjectRoleName, action: ProjectAction): boolean => PROJECT_ROLE_ACTIONS[role].has(action);

/**
Every action a team role grants, in the matrix's own declared order - used to describe a role
(e.g. an "Add member" dialog's role picker) without duplicating the matrix itself.
*/
export const getTeamRoleActions = (role: TeamRoleName): readonly TeamAction[] => [...TEAM_ROLE_ACTIONS[role]];

/**
Project-scope counterpart of {@link getTeamRoleActions}.
*/
export const getProjectRoleActions = (role: ProjectRoleName): readonly ProjectAction[] => [...PROJECT_ROLE_ACTIONS[role]];
